import { Location } from 'history';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ListControls } from '../types/list';
import { parseUrlParams } from '../util/listUrl';

interface UseUrlHydrationArgs {
  location: Location;
  controlsFromStore: Partial<ListControls>;
  onHydrate: (urlControls: Partial<ListControls>) => void;
}

interface UseUrlHydrationResult {
  hasHydrated: boolean;
}

export function useUrlHydration({
  location,
  controlsFromStore,
  onHydrate,
}: UseUrlHydrationArgs): UseUrlHydrationResult {
  const [hasHydrated, setHasHydrated] = useState(false);

  const urlParams = useMemo(
    () => parseUrlParams(location.search),
    [location.search]
  );

  const parseNumber = useCallback((value?: string | null) => {
    if (value == null) return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }, []);

  const urlControls = useMemo<Partial<ListControls>>(() => {
    return {
      page: parseNumber(urlParams.page),
      pageSize: parseNumber(urlParams.pageSize),
      search: urlParams.search,
      sort: urlParams.sort,
    };
  }, [
    parseNumber,
    urlParams.page,
    urlParams.pageSize,
    urlParams.search,
    urlParams.sort,
  ]);

  useEffect(() => {
    const hasAnyUrlParams =
      urlParams.page != null ||
      urlParams.pageSize != null ||
      urlParams.search != null ||
      urlParams.sort != null;

    const controlsMatch =
      (urlParams.page ?? String(controlsFromStore.page)) ===
        String(controlsFromStore.page) &&
      (urlParams.pageSize ?? String(controlsFromStore.pageSize)) ===
        String(controlsFromStore.pageSize) &&
      (urlParams.search ?? controlsFromStore.search ?? '') ===
        (controlsFromStore.search ?? '') &&
      (urlParams.sort ?? controlsFromStore.sort ?? '') ===
        (controlsFromStore.sort ?? '');

    if (hasAnyUrlParams) {
      if (!controlsMatch) {
        onHydrate(urlControls);
        setHasHydrated(false);
        return;
      }
      setHasHydrated(true);
      return;
    }

    // No URL params → consider hydrated using current store defaults
    setHasHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.search,
    controlsFromStore.page,
    controlsFromStore.pageSize,
    controlsFromStore.search,
    controlsFromStore.sort,
  ]);

  return { hasHydrated };
}
